import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, Dumbbell, HeartPulse, Sparkles, Flame, ArrowUpRight, MapPin, Phone, Clock } from "lucide-react";

const IMAGES = {
  hero: "https://images.pexels.com/photos/29807423/pexels-photo-29807423.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  physio: "https://images.pexels.com/photos/13538710/pexels-photo-13538710.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  fitness: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxneW0lMjBmaXRuZXNzJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzgzOTU4ODA0fDA&ixlib=rb-4.1.0&q=85",
  rehab: "https://images.unsplash.com/photo-1645005513713-9e2b92a687d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxyZWhhYmlsaXRhdGlvbiUyMGV4ZXJjaXNlfGVufDB8fHx8MTc4Mzk1ODgwNHww&ixlib=rb-4.1.0&q=85",
  spa: "https://images.unsplash.com/photo-1519415510236-718bdfcd89c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxmb290JTIwc3BhfGVufDB8fHx8MTc4Mzk1ODgwNHww&ixlib=rb-4.1.0&q=85",
  cupping: "https://images.pexels.com/photos/8312856/pexels-photo-8312856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const SERVICES = [
  { key: "physio", icon: HeartPulse, title: "Physiotherapy", desc: "Evidence-based, hands-on care to relieve pain and restore mobility.", img: IMAGES.physio },
  { key: "fitness", icon: Dumbbell, title: "Fitness", desc: "Guided strength & conditioning built around your body and goals.", img: IMAGES.fitness },
  { key: "rehab", icon: Activity, title: "Rehab", desc: "Structured post-injury and post-surgical rehabilitation programs.", img: IMAGES.rehab },
  { key: "spa", icon: Sparkles, title: "Foot Spa", desc: "Reflexology, warmth, and soft-tissue release for tired feet.", img: IMAGES.spa },
  { key: "cupping", icon: Flame, title: "Cupping", desc: "Traditional cupping therapy to release deep muscle tension and boost recovery.", img: IMAGES.cupping },
];

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-70" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="eyebrow flex items-center gap-3" data-testid="hero-eyebrow">
                <span className="inline-block w-8 h-px bg-emerald-900" />
                Physiofit Studio · Chennai
              </div>
              <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-stone-900 font-light">
                Restore your health, <br />
                <span className="text-emerald-900 italic font-normal">rebuild your strength</span>, <br />
                renew your life.
              </h1>
              <p className="mt-8 max-w-xl text-lg text-stone-600 leading-relaxed">
                A modern clinical studio in T. Nagar combining physiotherapy, targeted rehab, functional fitness and restorative foot spa — all under one roof.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/book"
                  data-testid="hero-book-cta"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-900 text-stone-50 font-medium hover:bg-emerald-950 transition-colors duration-200"
                >
                  Book an appointment
                  <ArrowUpRight size={18} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-services-cta"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-stone-300 text-stone-800 font-medium hover:border-emerald-900 hover:text-emerald-900 transition-colors duration-200"
                >
                  Explore services
                </Link>
              </div>
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                <Stat value="1.5k+" label="Sessions" />
                <Stat value="98%" label="Recovery rate" />
                <Stat value="12+" label="Years combined" />
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-stone-200 bg-white">
              <img src={IMAGES.hero} alt="Physiofit therapists at work" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-stone-50/95 backdrop-blur-sm border border-stone-200 p-4">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-600 animate-pulse" />
                  <span className="eyebrow">Now booking · This week</span>
                </div>
                <p className="mt-2 text-sm text-stone-700">Personalised assessment + first session in under 60 minutes.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="eyebrow">04 · Services</div>
              <h2 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-stone-900 font-light">Care that moves with you.</h2>
            </div>
            <Link to="/services" data-testid="services-view-all" className="text-sm text-emerald-900 hover:underline underline-offset-4">See all services →</Link>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s, idx) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="border border-stone-200 bg-white hover-lift"
                  data-testid={`service-card-${s.key}`}
                >
                  <div className="aspect-[16/9] overflow-hidden bg-stone-100">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6 sm:p-8">
                    <div className="flex items-center gap-3">
                      <span className="p-2 border border-stone-200 text-emerald-900"><Icon size={18} /></span>
                      <h3 className="font-heading text-2xl text-stone-900">{s.title}</h3>
                    </div>
                    <p className="mt-3 text-stone-600 leading-relaxed">{s.desc}</p>
                    <Link
                      to="/book"
                      data-testid={`service-book-${s.key}`}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-emerald-900 hover:gap-3 transition-all duration-200"
                    >
                      Book {s.title.toLowerCase()} <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24 border-t border-stone-200 bg-stone-100/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Approach</div>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl font-light text-stone-900 leading-tight">
              Slow diagnosis. <br />Precise treatment.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { n: "01", t: "Assess", d: "A full-body screen to understand posture, movement, pain triggers, and history." },
              { n: "02", t: "Plan", d: "A structured, milestone-driven roadmap tailored to your recovery timeline." },
              { n: "03", t: "Treat", d: "Manual therapy, guided exercise, dry needling, and modalities as needed." },
              { n: "04", t: "Sustain", d: "Home programs, education, and periodic check-ins to prevent relapse." },
            ].map((s) => (
              <div key={s.n} className="border border-stone-200 bg-white p-6" data-testid={`approach-step-${s.n}`}>
                <div className="font-mono text-xs text-emerald-900">{s.n}</div>
                <h4 className="mt-2 font-heading text-xl text-stone-900">{s.t}</h4>
                <p className="mt-2 text-stone-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-24 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow">Visit us</div>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl font-light text-stone-900 leading-tight">A walk-in at T. Nagar.</h2>
            <p className="mt-6 text-stone-600 leading-relaxed max-w-md">
              Right in the heart of Chennai's shopping district, our studio is a calm, well-equipped space designed to feel more like a wellness retreat than a clinic.
            </p>
            <div className="mt-10 space-y-4">
              <InfoRow icon={MapPin} label="Address" value="New No. 128, N Usman Road, T. Nagar, Chennai - 600017" />
              <InfoRow icon={Phone} label="Phone" value="+91 7200831203" href="tel:7200831203" testid="visit-phone" />
              <InfoRow icon={Clock} label="Hours" value={<>Open 7 days a week<br /><span className="text-stone-500 text-sm">10:00 AM – 7:00 PM</span></>} />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="aspect-[16/11] w-full border border-stone-200 overflow-hidden bg-stone-100">
              <iframe
                title="Physiofit Studio Location"
                src="https://www.google.com/maps?q=New%20No.%20128%20N%20Usman%20Road%20T%20Nagar%20Chennai&output=embed"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-stone-200 bg-emerald-950 text-stone-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow" style={{ color: "#A7F3D0" }}>Ready when you are</div>
              <h2 className="mt-3 font-heading text-4xl sm:text-6xl font-light leading-tight">
                Your body has been waiting.<br />Book a session today.
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/book"
                data-testid="footer-cta-book"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-stone-50 text-emerald-950 font-medium hover:bg-white transition-colors duration-200"
              >
                Book appointment <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-heading text-3xl text-stone-900">{value}</div>
      <div className="eyebrow mt-1">{label}</div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href, testid }) {
  const content = (
    <>
      <span className="p-2 border border-stone-200 text-emerald-900 shrink-0"><Icon size={16} /></span>
      <div>
        <div className="eyebrow">{label}</div>
        <div className="mt-1 text-stone-800">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} data-testid={testid} className="flex items-start gap-4 hover:text-emerald-900">{content}</a>
  ) : (
    <div className="flex items-start gap-4">{content}</div>
  );
}
